import Button from 'components/ui/Button/Button';
import Tabs from 'components/ui/Tabs/Tabs';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { FC, Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faInfoCircle, faRobot, faTimes } from '@fortawesome/free-solid-svg-icons';
import ProposalHtmlTripadvisor from './ProposalHtlTripadvisor';
import withErrorBoundary from '../../withErrorBoundary';
import GoogleHotelReviews from '../TripadvisorGoogle/GoogleHotelReviews';
import SingleProposalStyles from './SingleProposalStyles';
import useRecaptcha from 'hooks/useRecaptcha';
import { TripBuilderHotelDataDraftPayload } from 'utils/shared/tripResultBuilderPreview';
import { loadGoogleByExistingProposalHotel, loadTripadvisorByExistingProposalHotel } from 'services/proposal';
import ButtonsLang from '../TripadvisorGoogle/ButtonsLang';
import LoadingBlock from '../../LoadingBlock';
import { ProposalHotel } from 'ts/shared/interfaces/ProposalData';
import { useTranslation } from 'react-i18next';
import { ClaudeHotelInfoConfig, parseClaudeHotelInfoConfigs, resolveClaudeHotelInfoConfig } from 'utils/shared/claudeProposalInformation';
import { HotelInfoConfig, parseHotelInfoConfigs, resolveHotelInfo, resolveHotelInfoConfig } from 'utils/shared/hotelProposalInformation';
import ProposalHotelInformation from '../Create/ProposalHotelInformation';
import ProposalClaudeHotelInformation from '../Create/ProposalClaudeHotelInformation';
import { gIco, trIco } from 'constants/generic';

type GoogleDraft = {
  reviews: boolean;
  imagesIndexes: number[];
};

type TripadvisorDraft = {
  details: boolean;
  reviews: boolean;
  amenities: boolean;
};

export type ProposalHotelDataTab = 'hotel_info' | 'ai_hotel_info' | 'google' | 'tripadvisor';

interface ProposalHotelDataModalProps {
  show: boolean;
  onClose: () => void;
  hotelDataTab: ProposalHotelDataTab;
  setHotelDataTab: (tab: ProposalHotelDataTab) => void;
  currentHotel?: ProposalHotel;
  currency?: string;
  externalParam_1?: string;
  onSaveDraft: (payload: TripBuilderHotelDataDraftPayload) => void;
}

const createGoogleDraft = (
  selectedSerpHotel: any,
  sourceSerpHotel: any,
  googleChecked?: GoogleDraft,
): GoogleDraft => {
  if (googleChecked) {
    return {
      reviews: googleChecked.reviews ?? false,
      imagesIndexes: googleChecked.imagesIndexes ?? [],
    };
  }

  const sourceImages = sourceSerpHotel?.images || [];
  const selectedImages = selectedSerpHotel?.images || [];
  const imagesIndexes = sourceImages.reduce((acc: number[], sourceImage: any, index: number) => {
    const hasImage = selectedImages.some(
      (selectedImage: any) => selectedImage?.originalImage === sourceImage?.originalImage,
    );
    if (hasImage) {
      acc.push(index);
    }
    return acc;
  }, []);

  return {
    reviews: !!selectedSerpHotel?.reviews,
    imagesIndexes,
  };
};

const createTripadvisorDraft = (
  selectedTripadvisorHotel: any,
  tripAdvisorChecked?: TripadvisorDraft,
): TripadvisorDraft => {
  if (tripAdvisorChecked) {
    return {
      details: tripAdvisorChecked.details ?? false,
      reviews: tripAdvisorChecked.reviews ?? false,
      amenities: tripAdvisorChecked.amenities ?? false,
    };
  }

  return {
    details: !!selectedTripadvisorHotel?.description,
    reviews:
      !!selectedTripadvisorHotel?.numReviews && !!selectedTripadvisorHotel?.reviewRatingCount,
    amenities: !!selectedTripadvisorHotel?.amenities?.length,
  };
};

const hasGoogleSerpData = (serpHotel?: any) =>
  !!(serpHotel?.images?.length || serpHotel?.reviews);

const hasTripadvisorData = (tripAdvisorHotel?: any) =>
  !!(
    tripAdvisorHotel?.description ||
    tripAdvisorHotel?.amenities?.length ||
    tripAdvisorHotel?.numReviews
  );

const ProposalHotelDataModal: FC<ProposalHotelDataModalProps> = ({
  show,
  onClose,
  hotelDataTab,
  setHotelDataTab,
  currentHotel,
  currency,
  externalParam_1,
  onSaveDraft,
}) => {
  const {
    handleRecapcha } = useRecaptcha();
  const { t } = useTranslation();
  const [sourceSerpHotel, setSourceSerpHotel] = useState<any>();
  const [sourceTripadvisorHotel, setSourceTripadvisorHotel] = useState<any>();
  const [loadingGoogle, setLoadingGoogle] = useState('');
  const [loadingTripadvisor, setLoadingTripadvisor] = useState('');
  const [googleManuallyAttempted, setGoogleManuallyAttempted] = useState(false);
  const [tripadvisorManuallyAttempted, setTripadvisorManuallyAttempted] = useState(false);
  const [googleDraft, setGoogleDraft] = useState<GoogleDraft>({ reviews: false, imagesIndexes: [] });
  const [tripadvisorDraft, setTripadvisorDraft] = useState<TripadvisorDraft>({
    details: false,
    reviews: false,
    amenities: false,
  });
  const [claudeHotelInfoConfig, setClaudeHotelInfoConfig] = useState<ClaudeHotelInfoConfig | undefined>();
  const [hotelInfoConfig, setHotelInfoConfig] = useState<HotelInfoConfig | undefined>();
  const hasAiHotelInfo = !!currentHotel?.aI_HotelInformatiom?.content?.length;
  const hotelInfoData = resolveHotelInfo(currentHotel?.hotelInformatiom);
  const hasHotelInfo = !!hotelInfoData;

  const isGoogleLoading = !!loadingGoogle || !!currentHotel?.serpHotelEntireLoading;
  const isTripadvisorLoading = !!loadingTripadvisor || !!currentHotel?.tripAdvisorHotelEntireLoading;
  const sourceImages = sourceSerpHotel?.images || [];
  const hasGoogleData = hasGoogleSerpData(sourceSerpHotel);
  const hasTripadvisorContent = hasTripadvisorData(sourceTripadvisorHotel);
  const showGoogleNotFound =
    !isGoogleLoading &&
    !hasGoogleData &&
    (!!currentHotel?.serpHotelEntireRequested || googleManuallyAttempted);
  const showTripadvisorNotFound =
    !isTripadvisorLoading &&
    !hasTripadvisorContent &&
    (!!currentHotel?.tripAdvisorHotelEntireRequested || tripadvisorManuallyAttempted);

  useEffect(() => {
    if (!show || !currentHotel) {
      return;
    }

    const nextSourceSerp = currentHotel.serpHotelEntire || currentHotel.serpHotel;
    const nextSourceTripadvisor =
      currentHotel.tripAdvisorHotelEntire || currentHotel.tripAdvisorHotel;

    setGoogleManuallyAttempted(false);
    setTripadvisorManuallyAttempted(false);
    setSourceSerpHotel(nextSourceSerp);
    setSourceTripadvisorHotel(nextSourceTripadvisor);
    setGoogleDraft(
      createGoogleDraft(currentHotel.serpHotel || {}, nextSourceSerp, currentHotel.googleChecked),
    );
    setTripadvisorDraft(
      createTripadvisorDraft(currentHotel.tripAdvisorHotel || {}, currentHotel.tripAdvisorChecked),
    );
    const claudeConfigs = parseClaudeHotelInfoConfigs(externalParam_1);
    setClaudeHotelInfoConfig(
      currentHotel.claudeHotelInfoConfig ??
      resolveClaudeHotelInfoConfig(claudeConfigs, currentHotel, 0),
    );
    const hotelConfigs = parseHotelInfoConfigs(externalParam_1);
    setHotelInfoConfig(
      currentHotel.hotelInfoConfig ?? resolveHotelInfoConfig(hotelConfigs, currentHotel, 0),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentHotel?.hotel?.code, currentHotel?.rooms?.[0]?.startDate, externalParam_1]);

  useEffect(() => {
    if (!show || !currentHotel || loadingGoogle || googleManuallyAttempted) {
      return;
    }

    setSourceSerpHotel(currentHotel.serpHotelEntire || currentHotel.serpHotel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentHotel?.serpHotelEntire, currentHotel?.serpHotelEntireLoading, loadingGoogle, googleManuallyAttempted]);

  useEffect(() => {
    if (!show || !currentHotel || loadingTripadvisor || tripadvisorManuallyAttempted) {
      return;
    }

    setSourceTripadvisorHotel(currentHotel.tripAdvisorHotelEntire || currentHotel.tripAdvisorHotel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    currentHotel?.tripAdvisorHotelEntire,
    currentHotel?.tripAdvisorHotelEntireLoading,
    loadingTripadvisor,
    tripadvisorManuallyAttempted,
  ]);

  if (!show || !currentHotel) {
    return null;
  }

  const onClickLoadGoogle = async (lang: string) => {
    setLoadingGoogle(lang);
    setGoogleManuallyAttempted(true);
    try {
      const token = await handleRecapcha();
      if (!token) {
        return;
      }

      const googleData = await loadGoogleByExistingProposalHotel(currentHotel, lang, token, currency);
      setSourceSerpHotel(googleData?.serpResults?.serpHotel);
      setGoogleDraft({ reviews: false, imagesIndexes: [] });
    } finally {
      setLoadingGoogle('');
    }
  };

  const onClickLoadTripadvisor = async (lang: string) => {
    setLoadingTripadvisor(lang);
    setTripadvisorManuallyAttempted(true);
    try {
      const tripAdvisorData = await loadTripadvisorByExistingProposalHotel(currentHotel, lang);
      setSourceTripadvisorHotel(tripAdvisorData?.tripAdvisorHotel);
      setTripadvisorDraft({ details: false, reviews: false, amenities: false });
    } finally {
      setLoadingTripadvisor('');
    }
  };

  const onToggleGoogleReview = () => {
    setGoogleDraft((prev) => ({ ...prev, reviews: !prev.reviews }));
  };

  const onToggleGoogleImage = (index: number) => {
    setGoogleDraft((prev) => {
      const isSelected = prev.imagesIndexes.includes(index);
      const imagesIndexes = isSelected
        ? prev.imagesIndexes.filter((selectedIndex) => selectedIndex !== index)
        : [...prev.imagesIndexes, index];
      return { ...prev, imagesIndexes };
    });
  };

  const onToggleTripadvisor = (field: keyof TripadvisorDraft) => {
    setTripadvisorDraft((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const onClickSave = () => {
    if (hotelDataTab === 'hotel_info' || hotelDataTab === 'ai_hotel_info') {
      onSaveDraft({
        ...(hotelInfoConfig ? { hotelInfoConfig } : {}),
        ...(claudeHotelInfoConfig ? { claudeHotelInfoConfig } : {}),
      });
    } else {
      onSaveDraft({
        googleChecked: googleDraft,
        tripAdvisorChecked: tripadvisorDraft,
        sourceSerpHotel,
        sourceTripadvisorHotel,
      });
    }
    onClose();
  };

  return (
    <Fragment>
      <SingleProposalStyles />
      {createPortal(
        <div className="modal-overlay modal-overlay-top" onClick={onClose}>
          <div className="modal active" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <div className="title">{currentHotel.hotel?.name || t('hotel.hotel')}</div>
              <div>
                <FontAwesomeIcon icon={faTimes} onClick={onClose} />
              </div>
            </div>
            <div className="modal-content">
              <Tabs
                label={t('hotel.hotel')}
                value={hotelDataTab}
                onValueChange={(value) => setHotelDataTab(value as ProposalHotelDataTab)}
                className="text-sm"
                items={[
                  {
                    value: 'hotel_info',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faInfoCircle} size="sm" className="mr-5" />
                        {t('hotel.info')}
                      </>
                    ),
                  },
                  ...(hasAiHotelInfo
                    ? [
                        {
                          value: 'ai_hotel_info',
                          label: (
                            <>
                              <FontAwesomeIcon icon={faRobot} size="sm" className="mr-5" />
                              {t('proposal.ai_hotel_info')}
                            </>
                          ),
                        },
                      ]
                    : []),
                  {
                    value: 'google',
                    label: (
                      <span className="proposal-tab-label">
                        <img src={gIco} alt="g" />
                        WWW
                      </span>
                    ),
                  },
                  {
                    value: 'tripadvisor',
                    label: (
                      <span className="proposal-tab-label">
                        <img src={trIco} alt="t" />
                        TripAdvisor
                      </span>
                    ),
                  },
                ]}
              />
              {hotelDataTab === 'hotel_info' && hasHotelInfo && (
                <div className="tripadvisor-data">
                  <ProposalHotelInformation
                    hotelData={hotelInfoData}
                    config={hotelInfoConfig}
                    onChangeConfig={setHotelInfoConfig}
                    isEdit
                  />
                </div>
              )}
              {hotelDataTab === 'ai_hotel_info' && hasAiHotelInfo && (
                <div className="tripadvisor-data">
                  <ProposalClaudeHotelInformation
                    claudeData={currentHotel.aI_HotelInformatiom}
                    config={claudeHotelInfoConfig}
                    onChangeConfig={setClaudeHotelInfoConfig}
                  />
                </div>
              )}
              {hotelDataTab === 'google' && (
                <div className="tripadvisor-data">
                  <ButtonsLang onClickLoad={onClickLoadGoogle} loading={loadingGoogle} />
                  {isGoogleLoading && (
                    <div className="mt-20 text-bold">
                      <LoadingBlock />
                    </div>
                  )}
                  {!isGoogleLoading && showGoogleNotFound && (
                    <div className="mt-20 text-bold">Hotel was not found</div>
                  )}
                  {!isGoogleLoading && hasGoogleData && (
                    <>
                      <div className="text-sm">
                        <div className="mb-10">{t('proposal.add_to')}</div>
                        <Checkbox
                          className="mb-5"
                          checked={googleDraft.reviews}
                          label={t('proposal.reviews')}
                          onChange={onToggleGoogleReview}
                        />
                      </div>
                      <GoogleHotelReviews
                        res={{ serpResults: { serpHotel: sourceSerpHotel } } as any}
                      />
                      {sourceSerpHotel.description && <div className='title mt-20'>{t('hotel.description')}</div>}
                      <div className='text-lh text-sm mb-20 mt-10'>{sourceSerpHotel.description}</div>
                      {!!sourceImages.length && (
                        <div className="pr-photos-wr">
                          {sourceImages.map((im: any, index: number) => {
                            const isImageExists = googleDraft.imagesIndexes.includes(index);
                            return (
                              <div key={index} className="h-photos position-relative">
                                <img
                                  src={im.originalImage}
                                  alt="1"
                                  onError={(e) => ((e.target as any).parentNode.style.display = 'none')}
                                  style={{ pointerEvents: 'none' }}
                                />
                                <div className="pr-photos-checkbox">
                                  <div
                                    onClick={() => onToggleGoogleImage(index)}
                                    className={`image-checkbox ${isImageExists ? 'selected' : ''}`}
                                  >
                                    {isImageExists && <FontAwesomeIcon icon={faCheck} size="xs" />}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {hotelDataTab === 'tripadvisor' && (
                <div className="tripadvisor-data">
                  <ButtonsLang onClickLoad={onClickLoadTripadvisor} loading={loadingTripadvisor} />
                  {isTripadvisorLoading && (
                    <div className="mt-20 text-bold">
                      <LoadingBlock />
                    </div>
                  )}
                  {!isTripadvisorLoading && hasTripadvisorContent && (
                    <div className="text-sm">
                      <div className="mb-10">{t('proposal.add_to')}</div>
                      <Checkbox
                        className="mb-5"
                        checked={tripadvisorDraft.details}
                        label={t('proposal.details')}
                        onChange={() => onToggleTripadvisor('details')}
                      />
                      <Checkbox
                        className="mb-5"
                        checked={tripadvisorDraft.reviews}
                        label={t('proposal.reviews')}
                        onChange={() => onToggleTripadvisor('reviews')}
                      />
                      <Checkbox
                        className="mb-5"
                        checked={tripadvisorDraft.amenities}
                        label={t('proposal.amenities')}
                        onChange={() => onToggleTripadvisor('amenities')}
                      />
                    </div>
                  )}
                  {!isTripadvisorLoading && showTripadvisorNotFound && (
                    <div className="mt-20 text-bold">Hotel was not found</div>
                  )}
                  {!isTripadvisorLoading && (
                    <ProposalHtmlTripadvisor tripAdvisorHotel={sourceTripadvisorHotel} />
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button className="btn btn-default" title={t('close')} onClick={onClose} />
              <Button className="btn btn-primary" title={t('save')} onClick={onClickSave} />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </Fragment>
  );
};

export default withErrorBoundary(ProposalHotelDataModal, 'ProposalHotelDataModal');
